import type MonthYear from "src/interfaces/monthYear.interface";
import { formatMonthYear } from "src/utils/monthNames";
import type { IsRunningKey, ResumeSyncStatus } from "../types";
import { clearExpiredLease, type SyncLockOptions } from "./syncLock";
import { isLeaseExpired } from "./existingIdsByField";

export interface AbandonedResumeInput {
  integrationId: string;
  salesIsRunning?: number;
  inventoryIsRunning?: number;
  salesResume?: { start: MonthYear; end: MonthYear } | null;
  inventoryResume?: unknown | null;
  ttlMs: number;
  /** Display name in the banner, e.g. "Vinted" / "Depop". */
  platformLabel: string;
  now?: number;
}

/**
 * When a direct-import lease is expired and a checkpoint remains, build the
 * Resume banner status. Shared by Vinted and Depop.
 */
export function getAbandonedResumeStatus(
  input: AbandonedResumeInput
): ResumeSyncStatus | undefined {
  const now = input.now ?? Date.now();

  if (
    input.salesResume &&
    isLeaseExpired(input.salesIsRunning, now, input.ttlMs)
  ) {
    const { start, end } = input.salesResume;
    return {
      type: "resume",
      job: "sales-import",
      integrationId: input.integrationId,
      start,
      end,
      message: `A ${input.platformLabel} sales import (${formatMonthYear(
        start
      )}–${formatMonthYear(end)}) was interrupted. Resume where it left off?`,
    };
  }

  if (
    input.inventoryResume &&
    isLeaseExpired(input.inventoryIsRunning, now, input.ttlMs)
  ) {
    return {
      type: "resume",
      job: "inventory-import",
      integrationId: input.integrationId,
      message: `A ${input.platformLabel} inventory import was interrupted. Resume where it left off?`,
    };
  }

  return undefined;
}

/**
 * Wall-clock time when a checkpoint's matching lease expires
 * (`*IsRunning + ttlMs`), or undefined if no checkpoint sits under a still-fresh
 * lease. Used to schedule a one-shot banner recheck ~1s after expiry; heartbeats
 * refresh `*IsRunning` and must cancel + reschedule that timer.
 */
export function getActiveResumeLeaseExpiresAt(
  input: AbandonedResumeInput
): number | undefined {
  const now = input.now ?? Date.now();
  let earliest: number | undefined;

  const consider = (isRunning: number | undefined, hasResume: unknown) => {
    if (!hasResume || isLeaseExpired(isRunning, now, input.ttlMs)) return;
    if (isRunning == null || isRunning === 0) return;
    const expiresAt = isRunning + input.ttlMs;
    if (earliest == null || expiresAt < earliest) earliest = expiresAt;
  };

  consider(input.salesIsRunning, input.salesResume);
  consider(input.inventoryIsRunning, input.inventoryResume);
  return earliest;
}

export interface DiscardResumeFields {
  /** Firestore field holding the sales checkpoint map. */
  salesResumeField: string;
  /** Firestore field holding the inventory checkpoint map. */
  inventoryResumeField: string;
  ttlMs: number;
}

const JobLeaseKey: Record<ResumeSyncStatus["job"], IsRunningKey> = {
  "sales-import": "salesIsRunning",
  "inventory-import": "inventoryIsRunning",
};

/**
 * Dismissing the resume offer: drop the checkpoint plus the stale lease
 * (`*IsRunning` and its owner) so the offer is not re-derived from the
 * integration doc. No-op while a lease is still fresh (sync running elsewhere).
 */
export async function discardAbandonedResume(
  integrationId: string,
  job: ResumeSyncStatus["job"],
  fields: DiscardResumeFields,
  options: Pick<SyncLockOptions, "getDb" | "now"> = {}
): Promise<boolean> {
  const resumeField =
    job === "sales-import"
      ? fields.salesResumeField
      : fields.inventoryResumeField;
  return await clearExpiredLease(integrationId, [JobLeaseKey[job]], {
    ...options,
    ttlMs: fields.ttlMs,
    alsoDelete: [resumeField],
  });
}
